import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import {
  GraduationCap,
  Briefcase,
  Crown,
  Plant,
  Cpu,
  ArrowRight,
  MapPin,
  Users,
  CheckCircle,
  FunnelSimple,
  Globe,
  Handshake,
  TrendUp,
  FirstAidKit,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

function useCounter(end, duration = 2000, shouldAnimate = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!shouldAnimate) { setCount(0); return; }
    let startTime = null;
    let rafId;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) rafId = requestAnimationFrame(step);
    };
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [end, duration, shouldAnimate]);
  return count;
}

const categories = [
  { id: "all", label: "All Programs", icon: null },
  { id: "tvet", label: "TVET", icon: GraduationCap },
  { id: "enterprise", label: "Enterprise", icon: Briefcase },
  { id: "leadership", label: "Leadership", icon: Crown },
  { id: "agriculture", label: "Agriculture", icon: Plant },
  { id: "technology", label: "Technology", icon: Cpu },
  { id: "healthcare", label: "Healthcare", icon: FirstAidKit },
];

const programs = [
  // TVET Programs
  {
    id: "1",
    title: "Technical & Vocational Skills Training",
    category: "tvet",
    description: "Comprehensive hands-on training in technical and vocational skills, preparing youth for employment and entrepreneurship in various industries.",
    image: "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/zac9q79a_edited2-1.jpg",
    location: "Multiple Locations",
    participants: "500+ trained",
    status: "ongoing",
    features: ["Electrical & Electronics", "Welding & Fabrication", "Automotive Repair", "Construction Skills"],
  },
  {
    id: "2",
    title: "TVET Ecosystem Development",
    category: "tvet",
    description: "Building awareness and promoting Technical and Vocational Education Training as a pathway to employment and sustainable livelihoods.",
    image: "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/jmjxgcls_Understanding-the-TVET-ecosystem-in-Uganda.jpg",
    location: "Uganda",
    participants: "1000+ reached",
    status: "ongoing",
    features: ["School Outreach", "Career Guidance", "Skills Assessment", "Industry Partnerships"],
  },
  {
    id: "3",
    title: "Science & Laboratory Skills Program",
    category: "tvet",
    description: "Equipping students with practical laboratory and scientific skills essential for careers in science, technology, and healthcare.",
    image: "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/wyrbn3uv_Uganda-lead-photo.jpg",
    location: "Eastern Uganda",
    participants: "300+ students",
    status: "ongoing",
    features: ["Chemistry Labs", "Biology Practicals", "Physics Experiments", "Research Skills"],
  },
  {
    id: "4",
    title: "New TVET School – Bombo, Luweero",
    category: "tvet",
    description: "A modern skills training center equipping youth with practical vocational education, entrepreneurship, and agribusiness programs for self-reliance.",
    image: "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/afobkmcc_SCHOOL.png",
    location: "Bombo, Luweero District",
    participants: "Community-wide",
    status: "ongoing",
    features: ["Modern Classrooms", "Skills Workshops", "ICT Lab", "Youth Entrepreneurship"],
    badge: "new",
  },
  
  // Enterprise Programs
  {
    id: "5",
    title: "Community Enterprise Development",
    category: "enterprise",
    description: "Supporting small businesses and startups to grow sustainable enterprises through training, mentorship, and market access.",
    image: "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/lxiebqhu_55d8f7e2-761d-4c63-9839-a0652352eb5a.jpeg",
    location: "Central Uganda",
    participants: "200+ enterprises",
    status: "ongoing",
    features: ["Business Planning", "Financial Literacy", "Market Linkages", "Mentorship"],
  },
  {
    id: "6",
    title: "Youth Entrepreneurship Initiative",
    category: "enterprise",
    description: "Empowering young entrepreneurs to start and scale their businesses through training, funding support, and networking.",
    image: "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/zbcr8wiq_PicFrame3.jpg",
    location: "Multiple Regions",
    participants: "150+ youth",
    status: "ongoing",
    features: ["Startup Training", "Seed Funding", "Business Incubation", "Networking Events"],
  },
  
  // Leadership Programs
  {
    id: "7",
    title: "Student Leadership Development",
    category: "leadership",
    description: "Nurturing the next generation of leaders through comprehensive leadership training and mentorship programs in schools.",
    image: "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/cnkb2n0r_prefects-body-Serere-Campus-scaled.jpg",
    location: "Serere Campus & Others",
    participants: "300+ student leaders",
    status: "ongoing",
    features: ["Leadership Training", "Public Speaking", "Team Management", "Community Service"],
  },
  {
    id: "8",
    title: "Transformational Leadership Academy",
    category: "leadership",
    description: "Building community leaders who can drive sustainable development and positive change in their communities.",
    image: "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/m5g0m2r1_ph_20769_73858.jpg",
    location: "Kampala & Regions",
    participants: "100+ leaders",
    status: "ongoing",
    features: ["Executive Coaching", "Strategic Planning", "Change Management", "Governance"],
  },
  
  // Agriculture Programs
  {
    id: "9",
    title: "Youth in Agriculture Program",
    category: "agriculture",
    description: "Training young people in modern agricultural practices, agribusiness, and value chain development for sustainable farming.",
    image: "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/5qwhppbf_youth-in-agriculture.jpg",
    location: "Rural Uganda",
    participants: "400+ youth",
    status: "ongoing",
    features: ["Modern Farming", "Crop Management", "Livestock Rearing", "Agribusiness Skills"],
  },
  {
    id: "10",
    title: "Mango Farm Development Initiative",
    category: "agriculture",
    description: "Supporting mango farmers with training in orchard management, post-harvest handling, and market access for improved incomes.",
    image: "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/scoym4rc_Visit-to-a-mango-farm-1.jpg",
    location: "Northern Uganda",
    participants: "150+ farmers",
    status: "ongoing",
    features: ["Orchard Management", "Post-Harvest Handling", "Value Addition", "Market Access"],
  },
  {
    id: "11",
    title: "PRiDe Program - Women Rice Farmers",
    category: "agriculture",
    description: "Empowering women through rice farming cooperatives, providing training, inputs, and market linkages for sustainable livelihoods.",
    image: "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/djoqt4ti_PRiDe-II-Flagship-photo.jpg",
    location: "Eastern Uganda",
    participants: "250+ women",
    status: "ongoing",
    features: ["Rice Farming", "Cooperative Management", "Market Access", "Women Empowerment"],
  },
  {
    id: "12",
    title: "Passion Fruit Value Chain",
    category: "agriculture",
    description: "Supporting farmers in passion fruit cultivation, processing, and marketing to tap into the growing juice production industry.",
    image: "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/e0f592gf_11.jpg",
    location: "Central Uganda",
    participants: "100+ farmers",
    status: "ongoing",
    features: ["Cultivation Training", "Harvesting Techniques", "Processing", "Export Markets"],
  },
  
  // Technology Programs
  {
    id: "13",
    title: "Digital Skills & ICT Training",
    category: "technology",
    description: "Equipping youth and entrepreneurs with essential digital skills for the modern economy, including computer literacy and online business.",
    image: "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/0s4xigoz_ICT.png",
    location: "Urban & Rural Areas",
    participants: "350+ trained",
    status: "ongoing",
    features: ["Computer Basics", "Internet Skills", "Digital Marketing", "E-commerce"],
  },
  {
    id: "14",
    title: "Innovation & Startup Hub",
    category: "technology",
    description: "Supporting tech-enabled startups and innovations through incubation, mentorship, and access to resources.",
    image: "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/v6qw6xmc_ph_28265_101967.jpg",
    location: "Kampala",
    participants: "50+ startups",
    status: "ongoing",
    features: ["Incubation", "Tech Mentorship", "Investor Connects", "Product Development"],
  },

  // Healthcare Programs
  {
    id: "15",
    title: "Community Healthcare Center – Bombo Town",
    category: "healthcare",
    description: "Improving access to quality healthcare, preventive care, community wellness, and health worker training for underserved populations in Bombo and surrounding areas.",
    image: "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/fgzorv0v_HP.png",
    location: "Bombo Town, Luweero",
    participants: "Community-wide",
    status: "upcoming",
    features: ["Primary Healthcare", "Maternal & Child Health", "Preventive Care", "Health Education"],
    badge: "coming-soon",
  },
];

const impactStats = [
  {
    icon: Users,
    end: 3500,
    suffix: "+",
    label: "Participants Trained",
    description: "Youth and adults equipped with practical skills",
    color: "amber",
  },
  {
    icon: Globe,
    end: 45,
    suffix: "+",
    label: "Communities Reached",
    description: "Across Uganda and the broader East African region",
    color: "green",
  },
  {
    icon: Handshake,
    end: 200,
    suffix: "+",
    label: "Enterprises Supported",
    description: "Small businesses and startups empowered to grow",
    color: "cyan",
  },
];

function ImpactCounters() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <div ref={ref} className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
        className="text-center mb-16"
      >
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-4 block">
          Our Impact
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
          Transforming Lives Across Africa
        </h2>
        <p className="text-base text-green-100/70 max-w-2xl mx-auto">
          Through our programs, we have made measurable impact in communities, empowering individuals and building sustainable enterprises.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-6 lg:gap-10">
        {impactStats.map((stat, index) => (
          <ImpactCard key={stat.label} stat={stat} index={index} isInView={isInView} />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : {}}
        transition={{ delay: 1.2, duration: 0.8 }}
        className="mt-16 flex items-center justify-center gap-2"
      >
        <TrendUp className="h-5 w-5 text-green-400" weight="bold" />
        <span className="text-green-200/70 text-sm">Numbers growing every day as we expand our reach</span>
      </motion.div>
    </div>
  );
}

function ImpactCard({ stat, index, isInView }) {
  const count = useCounter(stat.end, 2200, isInView);
  const Icon = stat.icon;
  const bgMap = { amber: "bg-amber-500/10", green: "bg-green-500/10", cyan: "bg-cyan-500/10" };
  const textMap = { amber: "text-amber-400", green: "text-green-400", cyan: "text-cyan-400" };
  const borderMap = { amber: "border-amber-500/20", green: "border-green-500/20", cyan: "border-cyan-500/20" };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: 0.2 + index * 0.15, duration: 0.6 }}
      className={`relative rounded-2xl border ${borderMap[stat.color]} bg-white/[0.03] backdrop-blur-sm p-8 lg:p-10 text-center`}
      data-testid={`impact-counter-${index}`}
    >
      <div className={`w-16 h-16 rounded-2xl ${bgMap[stat.color]} flex items-center justify-center mx-auto mb-6`}>
        <Icon className={`h-8 w-8 ${textMap[stat.color]}`} weight="duotone" />
      </div>
      <div className="flex items-baseline justify-center gap-1 mb-2">
        <span className="text-5xl lg:text-6xl font-bold text-white tabular-nums">
          {count.toLocaleString()}
        </span>
        <span className={`text-3xl lg:text-4xl font-bold ${textMap[stat.color]}`}>{stat.suffix}</span>
      </div>
      <div className="text-lg font-semibold text-white mb-2">{stat.label}</div>
      <p className="text-sm text-green-100/50">{stat.description}</p>
    </motion.div>
  );
}

export default function ProgramsPage() {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [hoveredProgram, setHoveredProgram] = useState(null);

  const filteredPrograms = selectedCategory === "all" 
    ? programs 
    : programs.filter(p => p.category === selectedCategory);

  const getCategoryIcon = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.icon;
  };

  const getCategoryLabel = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.label || categoryId;
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-green-950" data-testid="programs-hero">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 pattern-dots" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-4 block">
              Our Programs
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-6">
              Programs & Initiatives
            </h1>
            <p className="text-lg text-green-100/80 leading-relaxed">
              Explore our ongoing programs that are transforming communities across Africa through TVET, enterprise development, leadership training, agriculture, and technology innovation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="sticky top-20 z-30 py-6 bg-white border-b border-slate-100 shadow-sm" data-testid="programs-filters">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4 lg:hidden">
            <FunnelSimple className="h-5 w-5 text-slate-500" weight="duotone" />
            <span className="text-sm font-medium text-slate-600">Filter by category:</span>
          </div>
          
          <div className="flex flex-wrap items-center justify-center gap-2 lg:gap-3">
            {categories.map((cat) => {
              const Icon = cat.icon;
              const isActive = selectedCategory === cat.id;
              const count = cat.id === "all" 
                ? programs.length 
                : programs.filter(p => p.category === cat.id).length;
              
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    isActive
                      ? "bg-green-700 text-white shadow-lg shadow-green-700/20"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                  data-testid={`filter-${cat.id}`}
                >
                  {Icon && <Icon className="h-4 w-4" weight={isActive ? "fill" : "duotone"} />}
                  <span>{cat.label}</span>
                  <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                    isActive ? "bg-white/20 text-white" : "bg-slate-200 text-slate-500"
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="programs-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedCategory}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {filteredPrograms.map((program, index) => {
                const CategoryIcon = getCategoryIcon(program.category);
                
                return (
                  <motion.article
                    key={program.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="group bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
                    onMouseEnter={() => setHoveredProgram(program.id)}
                    onMouseLeave={() => setHoveredProgram(null)}
                    data-testid={`program-card-${program.id}`}
                  >
                    {/* Image */}
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img 
                        src={program.image} 
                        alt={program.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent" />
                      
                      {/* Category Badge */}
                      <div className="absolute top-4 left-4 flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
                          program.category === "tvet" ? "bg-blue-500 text-white" :
                          program.category === "enterprise" ? "bg-purple-500 text-white" :
                          program.category === "leadership" ? "bg-amber-500 text-white" :
                          program.category === "agriculture" ? "bg-green-500 text-white" :
                          program.category === "healthcare" ? "bg-rose-500 text-white" :
                          "bg-cyan-500 text-white"
                        }`}>
                          {CategoryIcon && <CategoryIcon className="h-3.5 w-3.5" weight="fill" />}
                          {getCategoryLabel(program.category)}
                        </span>
                        {program.badge === "new" && (
                          <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-green-500 text-white animate-pulse">
                            NEW
                          </span>
                        )}
                        {program.badge === "coming-soon" && (
                          <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-cyan-500 text-white animate-pulse">
                            COMING SOON
                          </span>
                        )}
                      </div>

                      {/* Status Badge */}
                      <div className="absolute top-4 right-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${
                          program.status === "upcoming" ? "bg-amber-500 text-white" : "bg-green-500 text-white"
                        }`}>
                          {program.status}
                        </span>
                      </div>

                      {/* Location & Participants */}
                      <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                        <div className="flex items-center gap-1.5 text-white/90 text-sm">
                          <MapPin className="h-4 w-4" weight="fill" />
                          <span>{program.location}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-white/90 text-sm">
                          <Users className="h-4 w-4" weight="fill" />
                          <span>{program.participants}</span>
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <h3 className="text-xl font-semibold text-slate-900 mb-3 group-hover:text-green-700 transition-colors line-clamp-2">
                        {program.title}
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed mb-4 line-clamp-3">
                        {program.description}
                      </p>

                      {/* Features */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {program.features.slice(0, 3).map((feature) => (
                          <span 
                            key={feature}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-lg"
                          >
                            <CheckCircle className="h-3 w-3 text-green-600" weight="fill" />
                            {feature}
                          </span>
                        ))}
                        {program.features.length > 3 && (
                          <span className="px-2 py-1 bg-slate-100 text-slate-500 text-xs rounded-lg">
                            +{program.features.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.article>
                );
              })}
            </motion.div>
          </AnimatePresence>

          {filteredPrograms.length === 0 && (
            <div className="text-center py-20">
              <p className="text-slate-600 mb-4">No programs found in this category.</p>
              <Button 
                onClick={() => setSelectedCategory("all")}
                variant="outline"
                className="rounded-xl"
              >
                View All Programs
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Impact Counter Section */}
      <section className="relative py-20 md:py-28 bg-green-950 overflow-hidden" data-testid="programs-impact">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(217,119,6,0.08)_0%,_transparent_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(21,128,61,0.12)_0%,_transparent_60%)]" />
        </div>
        <ImpactCounters />
      </section>

      {/* Category Stats */}
      <section className="py-16 bg-white border-t border-slate-100" data-testid="programs-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-8 text-center">
            {categories.filter(c => c.id !== "all").map((cat) => {
              const Icon = cat.icon;
              const count = programs.filter(p => p.category === cat.id).length;
              
              return (
                <div key={cat.id} className="flex flex-col items-center">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-3 ${
                    cat.id === "tvet" ? "bg-blue-100" :
                    cat.id === "enterprise" ? "bg-purple-100" :
                    cat.id === "leadership" ? "bg-amber-100" :
                    cat.id === "agriculture" ? "bg-green-100" :
                    "bg-cyan-100"
                  }`}>
                    {Icon && <Icon className={`h-7 w-7 ${
                      cat.id === "tvet" ? "text-blue-600" :
                      cat.id === "enterprise" ? "text-purple-600" :
                      cat.id === "leadership" ? "text-amber-600" :
                      cat.id === "agriculture" ? "text-green-600" :
                      "text-cyan-600"
                    }`} weight="duotone" />}
                  </div>
                  <div className="text-3xl font-bold text-slate-900">{count}</div>
                  <div className="text-sm text-slate-500">{cat.label} Programs</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-gradient-to-r from-green-700 to-green-800" data-testid="programs-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Partner With Us on These Programs
          </h2>
          <p className="text-lg text-green-100/90 mb-8 leading-relaxed">
            We are always looking for partners to collaborate on community development programs. Whether you're a donor, government agency, or organization, let's work together to create lasting change.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              size="lg" 
              className="bg-amber-500 hover:bg-amber-600 text-green-950 font-semibold rounded-xl px-8"
              data-testid="programs-partner-button"
            >
              <Link to="/contact">
                Become a Partner <ArrowRight className="ml-2 h-4 w-4" weight="bold" />
              </Link>
            </Button>
            <Button 
              asChild 
              size="lg" 
              variant="outline" 
              className="bg-transparent border-white/30 text-white hover:bg-white/10 rounded-xl px-8"
              data-testid="programs-donate-button"
            >
              <Link to="/donate">Support Our Work</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
